import { Controller } from "@hotwired/stimulus"
import { isComponentRow } from "../helpers/position_grid_rows"

// The tree half of the position grid (e.g. billing/position_grid/_grid.html.erb).
//
// grid-cells owns the selection and the keyboard. This controller owns what turns its flat table
// into a tree — collapsing Titel, the toolbar search, the Art/Einheit/unvollständig filters and
// the sort (position_grid/_filters) — and claims the keys for collapsing through
// `grid-cells:command`, which grid-cells asks before it reads a key itself.
//
// Rows are flat, in tree pre-order, and say where they hang (`data-parent-id`). A row is hidden
// when an ancestor is collapsed; while a search or a filter runs, when neither it nor a descendant
// matches (a match keeps its Titel chain on screen, or it would float without context).
//
// The sort is a view: rows are reordered among their siblings, each with its subtree, and the
// order goes back to the OZ once it is switched off. While the list is searched, filtered or
// sorted (`data-position-tree-narrowed`), position-structure neither moves nor indents — a move
// among rows on screen would not be a move among the real siblings. New rows, copies and deletes
// name their place in the tree and go on.
//
// In the costing view (docs/plans/position-grid-inline-costing.md) a calculated position
// (`data-has-components`) has its Kalkulation as rows under it (`data-kind="component"`): it folds
// like a Titel, its rows follow it through the filters — a search also finds them by their own
// text — and the sort leaves them in their order under it.
//
// A row position-structure has just added or brought back stays on screen though the search or a
// filter would leave it out (`expand`'s `reveal`) — a new row vanishing as it lands reads as lost.
// It goes with the next change of the view.
//
// Collapsed Titel, filters and sort are kept per document in sessionStorage: every change
// re-renders the grid, and an overview springing back after each save would undo it every time.
const NUMERIC_SORT_KEYS = {
  quantity: "sortQuantity",
  unit_price: "sortUnitPrice",
  total: "sortTotal",
}

export default class extends Controller {
  static targets = [
    "row",
    "toggle",
    "search",
    "empty",
    "typeFilter",
    "unitFilter",
    "incompleteFilter",
    "sortKey",
    "sortDir",
    "counter",
    "reset",
    "foldAll",
  ]
  static values = { storageKey: String }

  connect() {
    this.query = ""
    this.revealed = new Set()
    this.pointerDown = false
    // Only rows that still fold: a position folded in the costing view folds nothing once the
    // view is off, and kept in the set it would leave the fold-all button reading "folded".
    this.collapsed = new Set(this.readStorage().filter((id) => foldable(this.row(id))))
    this.view = this.readView()
    this.restoreControls()
    this.onPointerDown = () => (this.pointerDown = true)
    this.onPointerUp = () => setTimeout(() => (this.pointerDown = false), 0)
    this.element.addEventListener("pointerdown", this.onPointerDown, true)
    document.addEventListener("pointerup", this.onPointerUp, true)
    this.onPointerOver = (event) => this.showLongText(event)
    this.element.addEventListener("pointerover", this.onPointerOver)
    this.apply()
  }

  disconnect() {
    this.element.removeEventListener("pointerdown", this.onPointerDown, true)
    document.removeEventListener("pointerup", this.onPointerUp, true)
    this.element.removeEventListener("pointerover", this.onPointerOver)
  }

  // The Langtext mark's tooltip, from the row's one copy of the text (`data-long-text-plain`),
  // set when the pointer first reaches the mark rather than rendered into every row.
  showLongText(event) {
    const icon = event.target.closest?.("[data-long-text-icon]")
    if (!icon || icon.title) return

    icon.title = icon.closest("tr")?.dataset.longTextPlain ?? ""
  }

  // A row streamed back after a cell save arrives unhidden and with its chevron open again. The
  // folding is this controller's state, not the server's, so it is applied anew. Rows connected in
  // one go share one pass; a save's several replaced rows do not quite — Turbo renders each stream
  // element after its own repaint, so they usually arrive one pass each. That is a handful of
  // passes per save, and each pass is plain DOM attribute work.
  rowTargetConnected() {
    if (!this.collapsed || this.applyQueued) return

    this.applyQueued = true
    queueMicrotask(() => {
      this.applyQueued = false
      this.apply()
    })
  }

  // A synced step replaces the Einheit filter alone (position_grid/_unit_filter): the unit chosen
  // goes back on the new select, or — gone from the LV — the filter goes with it. On connect
  // (`view` not read yet) restoreControls does this.
  unitFilterTargetConnected(select) {
    if (!this.view) return

    this.restoring = true
    this.setSelect(select, this.view.unit)
    this.restoring = false
    if (select.value !== this.view.unit) this.dropUnitFilter()
  }

  // The last position with a unit is gone, and the select with it. Not when the whole grid leaves
  // the page: the view stays remembered for its return.
  unitFilterTargetDisconnected() {
    queueMicrotask(() => {
      if (this.element.isConnected && !this.hasUnitFilterTarget && this.view.unit)
        this.dropUnitFilter()
    })
  }

  dropUnitFilter() {
    this.view.unit = ""
    this.persistView()
    this.apply()
  }

  get rows() {
    return Array.from(this.element.querySelectorAll("tr[data-grid-row]"))
  }

  row(id) {
    return this.element.querySelector(`tr[data-grid-row][data-row-id="${id}"]`)
  }

  // -- keys -------------------------------------------------------------------

  command(event) {
    const { originalEvent: key, rowId, column } = event.detail
    const mod = key.ctrlKey || key.metaKey
    const horizontal = key.key === "ArrowLeft" || key.key === "ArrowRight"
    const vertical = key.key === "ArrowUp" || key.key === "ArrowDown"

    if (key.altKey && !key.shiftKey && !mod && horizontal) {
      event.preventDefault()
      // A search or filter shows every match whatever is collapsed, so a fold now would only
      // change what reappears once it is cleared — nothing the user could see happen.
      if (!this.filtering) this.collapseFrom(rowId, key.key === "ArrowLeft")
    } else if (mod && !key.altKey && !key.shiftKey && vertical) {
      event.preventDefault()
      this.jumpToGroup(rowId, column, key.key === "ArrowUp" ? -1 : 1)
    }
  }

  // Alt+Left on a Titel folds it, on a position folds the Titel it sits in — the selection then
  // moves up onto that Titel, as in a file tree. Alt+Right only ever opens.
  collapseFrom(rowId, close) {
    const row = this.row(rowId)
    if (!row) return

    if (foldable(row)) {
      this.setCollapsed(rowId, close)
    } else if (close && row.dataset.parentId) {
      this.setCollapsed(row.dataset.parentId, true)
    }
  }

  jumpToGroup(rowId, column, direction) {
    const visible = this.rows.filter((row) => !row.hidden)
    let index = visible.findIndex((row) => row.dataset.rowId === rowId) + direction
    while (visible[index] && visible[index].dataset.kind !== "group") index += direction

    const target = visible[index]?.querySelector(`td[data-column="${CSS.escape(column)}"]`)
    if (target) this.selectCell(target)
  }

  // -- collapsing -------------------------------------------------------------

  toggle(event) {
    if (this.filtering) return

    const id = String(event.params.id)
    this.setCollapsed(id, !this.collapsed.has(id))
  }

  // Both end a running search: asking for the whole tree folded or unfolded means the tree, and
  // under a search the fold would not show.
  expandAll() {
    this.endSearch()
    this.collapsed.clear()
    this.persist()
  }

  collapseAll() {
    this.endSearch()
    this.rows
      .filter((row) => row.dataset.kind === "group")
      .forEach((row) => this.collapsed.add(row.dataset.rowId))
    this.persist()
  }

  // The toolbar's one button for both: it unfolds everything while any Titel is folded, and
  // folds them all otherwise. Like the two it replaces, it ends a running search.
  toggleAll() {
    if (this.showsFolded) this.expandAll()
    else this.collapseAll()
  }

  // Its label says what a click does next; without a Titel there is nothing to fold.
  renderFoldAll() {
    if (!this.hasFoldAllTarget) return

    const button = this.foldAllTarget
    const folded = this.showsFolded
    const label = folded ? button.dataset.expandLabel : button.dataset.collapseLabel
    button.title = label
    button.setAttribute("aria-label", label)
    button.toggleAttribute("data-folded", folded)
    button.disabled = !this.rows.some((row) => row.dataset.kind === "group")
  }

  // position-structure opens a Titel it is about to put a row into: a row added or indented into
  // a folded Titel would vanish the moment it lands. A row it selects that is still hidden comes
  // as `reveal`.
  expand(event) {
    event.detail.ids.forEach((id) => this.collapsed.delete(String(id)))
    if (event.detail.reveal) this.revealed.add(String(event.detail.reveal))
    this.persist()
  }

  setCollapsed(id, collapsed) {
    if (collapsed) this.collapsed.add(id)
    else this.collapsed.delete(id)
    this.persist()
  }

  persist() {
    try {
      sessionStorage.setItem(this.storageKeyValue, JSON.stringify([...this.collapsed]))
    } catch {
      // Storage unavailable (private mode): the collapse still applies, it just is not remembered.
    }
    this.apply()
  }

  readStorage() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(this.storageKeyValue) || "[]")
      return Array.isArray(stored) ? stored.map(String) : []
    } catch {
      return []
    }
  }

  // -- search -----------------------------------------------------------------

  // Whitespace folded to single spaces, as the rows' `data-search` is. The other controls are read
  // here too: every one of them calls this on change.
  filter() {
    if (this.restoring) return
    this.revealed.clear()
    this.query = this.searchTarget.value.trim().replace(/\s+/g, " ").toLowerCase()
    this.view = {
      type: this.hasTypeFilterTarget ? this.typeFilterTarget.value : "",
      unit: this.hasUnitFilterTarget ? this.unitFilterTarget.value : "",
      incomplete: this.hasIncompleteFilterTarget && this.incompleteFilterTarget.checked,
      sortKey: this.hasSortKeyTarget ? this.sortKeyTarget.value : "standard",
      sortDir: this.view.sortDir,
    }
    this.persistView()
    this.apply()
  }

  endSearch() {
    if (this.hasSearchTarget) this.searchTarget.value = ""
    this.query = ""
  }

  toggleSortDir() {
    this.revealed.clear()
    this.view.sortDir = this.view.sortDir === "desc" ? "asc" : "desc"
    this.persistView()
    this.apply()
  }

  // Back to the whole tree in OZ order.
  reset() {
    this.revealed.clear()
    this.endSearch()
    this.view = defaultView()
    this.restoreControls()
    this.persistView()
    this.apply()
  }

  get filtering() {
    return !!(this.query || this.view.type || this.view.unit || this.view.incomplete)
  }

  // A search shows its matches unfolded whatever is collapsed — the toolbar's button then offers
  // to fold, as every chevron reads as open. The button folds Titel, so a position's folded
  // Kalkulation (the costing view) does not count as folded here.
  get showsFolded() {
    return (
      !this.filtering && [...this.collapsed].some((id) => this.row(id)?.dataset.kind === "group")
    )
  }

  get sorting() {
    return this.view.sortKey !== "standard"
  }

  matches(row, { search = true } = {}) {
    const data = row.dataset
    if (search && this.query && !this.searchText(row).includes(this.query)) return false
    if (this.view.type && data.positionType !== this.view.type) return false
    if (this.view.unit && data.unit !== this.view.unit) return false
    if (this.view.incomplete && data.incomplete !== "1") return false
    return true
  }

  // OZ and Kurztext (`data-search`, lower-cased by the server) and the Langtext, lower-cased once per
  // row and kept on the element — a row replaced by the server is a new element and starts afresh.
  searchText(row) {
    row.positionGridSearch ??= [row.dataset.search, row.dataset.longTextPlain]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
    return row.positionGridSearch
  }

  readView() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(`${this.storageKeyValue}-view`) || "{}")
      return { ...defaultView(), ...stored }
    } catch {
      return defaultView()
    }
  }

  persistView() {
    try {
      sessionStorage.setItem(`${this.storageKeyValue}-view`, JSON.stringify(this.view))
    } catch {
      // Storage unavailable: the view applies, it just is not remembered.
    }
  }

  // A SlimSelect announces a value set through its store as a change, which would read the controls
  // not set yet back into the view; nothing is read until all of them are.
  restoreControls() {
    this.restoring = true
    if (this.hasTypeFilterTarget) this.setSelect(this.typeFilterTarget, this.view.type)
    if (this.hasUnitFilterTarget) this.setSelect(this.unitFilterTarget, this.view.unit)
    if (this.hasIncompleteFilterTarget) this.incompleteFilterTarget.checked = this.view.incomplete
    if (this.hasSortKeyTarget) this.setSelect(this.sortKeyTarget, this.view.sortKey)
    this.restoring = false
    // A unit no longer in the LV drops out of its select; the filter goes with it.
    if (this.hasUnitFilterTarget && this.unitFilterTarget.value !== this.view.unit)
      this.view.unit = ""
  }

  // A select already turned into a SlimSelect reverts a plain `.value` write on its next change, so
  // it is set through its store.
  setSelect(select, value) {
    const slim = this.application.getControllerForElementAndIdentifier(select, "select")?.slimselect
    if (slim) slim.setSelected(value, false)
    select.value = value
  }

  // -- sorting ----------------------------------------------------------------

  // Siblings in the chosen order, each followed by its subtree; the OZ order (`data-position`)
  // when no sort is on. Rows are moved only when the order differs: a moved row reconnects as a
  // target, which asks for another pass.
  sortRows(rows) {
    const children = new Map()
    rows.forEach((row) => {
      const parent = row.dataset.parentId || ""
      if (!children.has(parent)) children.set(parent, [])
      children.get(parent).push(row)
    })
    const ordered = []
    const walk = (parent) => {
      const siblings = (children.get(parent) || []).map((row, index) => ({ row, index }))
      siblings.sort((a, b) => this.compareRows(a, b))
      siblings.forEach(({ row }) => {
        ordered.push(row)
        walk(row.dataset.rowId)
      })
    }
    walk("")
    if (ordered.length !== rows.length || ordered.every((row, index) => row === rows[index]))
      return rows

    const body = rows[0].parentElement
    const tail = this.hasEmptyTarget ? this.emptyTarget : null
    ordered.forEach((row) => body.insertBefore(row, tail))
    return ordered
  }

  compareRows(a, b) {
    // A position's Kalkulation rows keep their order under it, whatever the sort.
    if (isComponentRow(a.row)) return a.index - b.index
    if (!this.sorting) {
      return Number(a.row.dataset.position) - Number(b.row.dataset.position) || a.index - b.index
    }

    const direction = this.view.sortDir === "desc" ? -1 : 1
    if (this.view.sortKey === "text") {
      const order = (a.row.dataset.sortText || "").localeCompare(b.row.dataset.sortText || "", "de")
      return order !== 0 ? order * direction : a.index - b.index
    }
    const attribute = NUMERIC_SORT_KEYS[this.view.sortKey]
    const left = parseFloat(a.row.dataset[attribute])
    const right = parseFloat(b.row.dataset[attribute])
    // Rows without a value last, whichever the direction.
    if (Number.isNaN(left) || Number.isNaN(right)) {
      return Number.isNaN(left) - Number.isNaN(right) || a.index - b.index
    }
    return left === right ? a.index - b.index : (left - right) * direction
  }

  // Escape in a non-empty search clears it; in an empty one it is left to whatever else wants it.
  clearFilter(event) {
    if (!this.searchTarget.value) return

    event.preventDefault()
    this.searchTarget.value = ""
    this.filter()
  }

  // -- rendering --------------------------------------------------------------

  apply() {
    const rows = this.rows.length ? this.sortRows(this.rows) : []
    const parentOf = new Map(rows.map((row) => [row.dataset.rowId, row.dataset.parentId || null]))
    const ancestors = (id) => {
      const chain = []
      for (let parent = parentOf.get(id); parent; parent = parentOf.get(parent)) chain.push(parent)
      return chain
    }

    const filtering = this.filtering
    let matched = 0
    if (filtering) {
      const shown = new Set()
      // A Titel found by its own name counts as a match like a position — it is on screen and
      // marked as one. A Titel shown only for a match beneath it is not.
      const matchedIds = new Set()
      const show = (row) => {
        shown.add(row.dataset.rowId)
        ancestors(row.dataset.rowId).forEach((id) => shown.add(id))
      }
      rows.forEach((row) => {
        if (isComponentRow(row) || !this.matches(row)) return
        matched += 1
        matchedIds.add(row.dataset.rowId)
        show(row)
      })
      // A Kalkulation row comes with its position when that matched or was revealed, and is a match
      // of its own when the search finds its text under a position the other filters let through.
      const rowsById = new Map(rows.map((row) => [row.dataset.rowId, row]))
      rows.forEach((row) => {
        if (!isComponentRow(row)) return
        const parentId = row.dataset.parentId
        if (matchedIds.has(parentId) || this.revealed.has(parentId)) return show(row)

        const position = rowsById.get(parentId)
        if (!this.query || !position || !this.searchText(row).includes(this.query)) return
        if (!this.matches(position, { search: false })) return
        matched += 1
        show(row)
      })
      this.revealed.forEach((id) => {
        if (rowsById.has(id)) show(rowsById.get(id))
      })
      rows.forEach((row) => (row.hidden = !shown.has(row.dataset.rowId)))
    } else {
      rows.forEach(
        (row) => (row.hidden = ancestors(row.dataset.rowId).some((id) => this.collapsed.has(id))),
      )
    }

    // A search shows its matches whatever is collapsed, so every chevron reads as open meanwhile
    // and is switched off until the search is cleared.
    this.toggleTargets.forEach((toggle) => {
      const open = filtering || !this.collapsed.has(toggle.closest("tr").dataset.rowId)
      toggle.setAttribute("aria-expanded", String(open))
      toggle.disabled = filtering
    })
    this.element.toggleAttribute("data-position-tree-narrowed", filtering || this.sorting)
    // While it is set, a hidden row is one the search or a filter left out rather than one under a
    // folded Titel — boq-bulk ticks no such row with its Titel.
    this.element.toggleAttribute("data-position-tree-filtered", filtering)
    this.renderViewControls(filtering, matched)
    this.renderFoldAll()
    if (this.hasEmptyTarget && rows.length)
      this.emptyTarget.hidden = rows.some((row) => !row.hidden)

    this.keepSelectionVisible(ancestors)
    this.keepTabStop()
  }

  renderViewControls(filtering, matched) {
    if (this.hasCounterTarget) {
      const template = this.counterTarget.dataset[matched === 1 ? "matchesOne" : "matchesOther"]
      this.counterTarget.textContent = (template || "").replace("%{count}", matched)
      this.counterTarget.hidden = !filtering
    }
    if (this.hasResetTarget) this.resetTarget.hidden = !(filtering || this.sorting)
    if (this.hasSortDirTarget) {
      this.sortDirTarget
        .querySelector("svg")
        ?.classList.toggle("rotate-180", this.view.sortDir === "desc")
    }
  }

  // A selection inside a Titel that was just folded moves up onto the nearest Titel still on
  // screen, in the same column — otherwise the arrow keys would start from a row nobody can see.
  keepSelectionVisible(ancestors) {
    const selected = this.element.querySelector("td[data-selected]")
    const row = selected?.closest("tr")
    if (!row?.hidden) return

    const visibleAncestor = ancestors(row.dataset.rowId)
      .map((id) => this.row(id))
      .find((candidate) => candidate && !candidate.hidden)
    const target = visibleAncestor?.querySelector(
      `td[data-column="${CSS.escape(selected.dataset.column)}"]`,
    )
    if (target) this.selectCell(target)
  }

  // Exactly one visible cell is a tab stop, so Tab from the toolbar lands in the table. grid-cells
  // moves it along with the selection; this only fills in when there is none, or it was hidden.
  keepTabStop() {
    const current = this.element.querySelector('td[data-column][tabindex="0"]')
    if (current && !current.closest("tr").hidden) return

    current?.setAttribute("tabindex", "-1")
    this.rows
      .find((row) => !row.hidden)
      ?.querySelector("td[data-column]")
      ?.setAttribute("tabindex", "0")
  }

  // Tabbing into the table selects the cell that took focus. Only keyboard focus: a mouse click
  // focuses the cell too, and grid-cells already selects on that click.
  enter(event) {
    if (this.pointerDown) return

    const cell = event.target
    if (!cell.matches?.("td[data-column]") || cell.hasAttribute("data-selected")) return

    this.selectCell(cell)
  }

  // Through a click, so grid-cells does what it does for any selection: commits an open editor,
  // resets a Tab run, moves the tab stop and scrolls the cell into view. That click also focuses
  // the cell, which must not happen while the user is typing a search — a keystroke that hides
  // the selected row moves the selection, and the next keystroke would land in the table.
  selectCell(cell) {
    const typing = this.hasSearchTarget && document.activeElement === this.searchTarget
    cell.click()
    if (typing) this.searchTarget.focus()
  }
}

// A Titel, or a position whose Kalkulation shows as rows under it (`data-has-components`).
function foldable(row) {
  if (!row) return false
  return (
    row.dataset.kind === "group" ||
    (row.dataset.kind === "position" && row.dataset.hasComponents !== undefined)
  )
}

function defaultView() {
  return { type: "", unit: "", incomplete: false, sortKey: "standard", sortDir: "asc" }
}
